import {
  AgCartesianAxisOptions,
  AgCartesianSeriesOptions,
  AgChartOptions,
  AgLineSeriesOptions,
} from 'ag-charts-enterprise';
import { AgCharts } from 'ag-charts-react';
import dayjs from 'dayjs';
import { TFunction, useTranslation } from 'translations/hooks';
import { getAgChartLocale } from 'translations/locale';

import { PlotterMode } from 'business/data-analysis/constants';
import {
  RingSerieRendererParams,
  RingSerieOptions,
  ChartType,
} from 'business/data-analysis/pages/graph/components/chart/types';
import {
  GraphPageState,
  PeriodParameter,
  RingParameter,
} from 'business/data-analysis/pages/graph/hooks/graph-context/types';
import { useGraphSetData } from 'business/data-analysis/pages/graph/hooks/graph-data-context/contexts';
import { GraphSetData } from 'business/data-analysis/pages/graph/hooks/graph-data-context/types';
import { useGraph } from 'business/data-analysis/pages/graph/hooks/use-graph';
import { useMode } from 'business/data-analysis/pages/graph/hooks/use-mode';
import {
  parseAndFormatDateForGraph,
  parseAndFormatDateForMobileGraph,
} from 'business/data-analysis/services/parse-and-format-date-for-graph';
import { useMultipleGraphRefsContext } from 'business/providers/graph-reference';
import { useChartResize } from 'technical/chart-resize';
import { useMediaType } from 'technical/media/hooks';

import styles from './index.module.scss';

const OPACITY = 0.7;

interface Props {
  graphId: string;
  initialZoom?: boolean;
}

type Parameter = Parameters<
  Parameters<
    GraphPageState['structure']['graphs']['set']
  >[1]['parameters']['set']
>[1];

const isRingParameter = (parameter: any): parameter is RingParameter => {
  return parameter.aggregationType && parameter.computeType;
};

const getParameterName = (parameter: Parameter, t: TFunction) => {
  if (isRingParameter(parameter)) {
    return `${parameter.name} (${parameter.unit === '' ? '-' : parameter.unit}) - ${t('dataAnalysis.ringMode.computeType.type', { context: parameter.computeType })} - ${t('dataAnalysis.ringMode.aggregationType.type', { context: parameter.aggregationType })}`;
  }

  return `${parameter.name} (${parameter.unit === '' ? '-' : parameter.unit})`;
};

const getTimePeriodSerie = (
  parameter: Parameter,
  t: TFunction,
  language: string,
) => {
  return {
    type: ChartType.LINE,
    xKey: 'date',
    yKey: parameter.id.toString(),
    yName: getParameterName(parameter, t),
    marker: {
      stroke: parameter.color,
      fill: parameter.color,
      size: 4,
    },
    stroke: parameter.color,
    strokeOpacity: OPACITY,
    connectMissingData: true,
    id: parameter.key,
    tooltip: {
      renderer: (params) => {
        const tootlip = {
          data: [
            {
              label: parameter.name,
              value: `${Number(params.datum[params.yKey]).toLocaleString(
                language,
              )} ${parameter.unit}`,
            },
          ],
        };
        return tootlip;
      },
    },
  } satisfies AgLineSeriesOptions;
};

const formatRingParameterName = (parameter: RingParameter) => {
  return `${parameter.id}_${parameter.aggregationType}_${parameter.computeType}`;
};

const getRingSerie = (parameter: Parameter, t: TFunction, language: string) => {
  return {
    type: parameter.chartType,
    xKey: 'ring',
    yKey: formatRingParameterName(parameter as RingParameter),
    yName: getParameterName(parameter, t),
    marker: {
      stroke: parameter.color,
      fill: parameter.color,
      size: 4,
    },
    stroke: parameter.color,
    strokeOpacity: OPACITY,
    fill: parameter.color,
    fillOpacity: OPACITY,
    connectMissingData: true,
    id: parameter.key,
    tooltip: {
      renderer: (params: RingSerieRendererParams) => {
        const tootlip = {
          data: [
            {
              label: parameter.name,
              value: `${Number(params.datum[params.yKey]).toLocaleString(
                language,
              )} ${parameter.unit}`,
            },
          ],
        };
        return tootlip;
      },
    },
  } satisfies RingSerieOptions;
};

const isPeriodData = (
  mode: PlotterMode,
  _data: GraphSetData[number],
): _data is { date: string } & Record<string, string> => {
  return mode === PlotterMode.TIME_PERIOD;
};

const isRingData = (
  mode: PlotterMode,
  _data: GraphSetData[number],
): _data is { ring: number } & Record<string, string> => {
  return mode === PlotterMode.RING;
};

const transformData = (data: GraphSetData, mode: PlotterMode) => {
  return data.map((value) => {
    const values = Object.entries(value).reduce((prev, [key, val]) => {
      if (key === 'date') {
        return prev;
      }
      return { ...prev, [key]: parseFloat(val?.toString()) };
    }, {});

    return {
      ...values,
      date: isPeriodData(mode, value)
        ? dayjs.tz(value.date).toDate()
        : undefined,
      ring: isRingData(mode, value) ? value.ring : undefined,
    };
  });
};

const getAxes = (
  mode: PlotterMode,
  isMobile: boolean,
  parameters: (PeriodParameter | RingParameter)[],
) => {
  return mode === PlotterMode.TIME_PERIOD
    ? ([
        {
          label: {
            formatter: (params) =>
              isMobile
                ? parseAndFormatDateForMobileGraph(params.value)
                : parseAndFormatDateForGraph(params.value),
          },
          keys: ['date'],
          type: 'time',
          position: 'bottom',
          nice: false,
        },
        {
          keys: parameters.map((param) => param.id.toString()),
          position: 'left',
          type: 'number',
        },
      ] satisfies AgCartesianAxisOptions[])
    : ([
        {
          keys: ['ring'],
          type: 'category',
          position: 'bottom',
        },
        {
          keys: parameters.map((param) =>
            formatRingParameterName(param as RingParameter),
          ),
          position: 'left',
          type: 'number',
        },
      ] satisfies AgCartesianAxisOptions[]);
};

const Chart = ({ graphId, initialZoom }: Props) => {
  const { getRef } = useMultipleGraphRefsContext();
  const { isMobile } = useMediaType();
  const ref = getRef(graphId);

  const mode = useMode();
  const graph = useGraph(graphId);
  const values = useGraphSetData();
  const { t, language } = useTranslation();

  const getSerie =
    mode === PlotterMode.TIME_PERIOD ? getTimePeriodSerie : getRingSerie;

  const parameters = Array.from(graph.parameters.values());

  const series = parameters
    .filter((parameter): parameter is Parameter => !!parameter)
    .map((parameter): AgCartesianSeriesOptions => {
      return getSerie(parameter, t, language);
    });

  const axes = getAxes(mode, isMobile, parameters);

  const data = transformData(values, mode);

  const chartOptions: AgChartOptions = {
    data,
    axes,
    series,
    animation: {
      enabled: false,
    },
    locale: {
      localeText: getAgChartLocale(language),
    },
    ...(isMobile
      ? {
          height: 500,
          touch: { dragAction: 'drag' },
          zoom: {
            enabled: true,
            axes: 'x',
            autoScaling: { enabled: false },
            enableAxisDragging: false,
            enableTwoFingerZoom: true,
            buttons: {
              enabled: false,
            },
          },
          initialState: {
            zoom: initialZoom
              ? {
                  ratioX: {
                    start: 0,
                    end: 0.5,
                  },
                }
              : {},
          },
          contextMenu: {
            enabled: false,
          },
          legend: {
            enabled: true,
            maxHeight: 60,
          },
        }
      : {
          contextMenu: {
            enabled: true,
          },
          zoom: {
            // zoom on where the mouse is
            anchorPointX: 'pointer',
            anchorPointY: 'pointer',
          },
          legend: {
            enabled: true,
          },
        }),
  };

  useChartResize(ref);

  return (
    <div className={styles.chartContainer}>
      <AgCharts options={chartOptions} ref={ref} />
    </div>
  );
};
export default Chart;
